import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";

import ScreenContainer from "../../Components/ScreenContainer";
import EntityDetailView from "../../PagesContent/AssetManagement/Network/EntityDetailView";
import useEntityTypeConfig from "../../PagesContent/AssetManagement/Network/hooks/useEntityTypeConfig";
import CreateEditEntityDialog from "../../PagesContent/AssetManagement/Network/CreateEditEntityDialog";
import { CancelFormActionButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import { getNetworkEntity } from "../../Api/NetworkEntityApi";
import { FORM_TOKENS } from "../../PagesContent/AssetManagement/WorkOrders/WorkOrderFormShared";
import { usePageMeta } from "../../Hooks/usePageMeta";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import CODE from "../../Static/Constants/StatusCodes";

// One network entity, on its own URL.
//
// WHY A ROUTE, having argued against one. The detail view began as a dialog
// opened from the Network tab's own row, which is the module's convention --
// AssetDetailView and WorkOrderDetailView are both FullScreenDialogs owned by
// the tab that lists them. A dialog is fine for a thing reached from ONE
// place.
//
// This is now reached from two. The Assets tab's Current Custody cell links
// here, and so does the custody column inside this very page. A thing linked
// to from elsewhere needs an address: without one, "open the customer" from
// the Assets tab would mean navigating to the Network tab, finding the row and
// clicking it, or lifting one tab's dialog state into the module shell so
// another tab could open it.
//
// IT RENDERS IN THE SHELL, not over it. EntityDetailView was a
// FullScreenDialog, which is the right shape for a detail view opened from the
// row that lists it -- but a slide-over dims the nav the user still needs,
// carries an X whose destination nobody can guess, and animates a panel over a
// page that was never there when the URL is opened directly. This page fetches
// the entity and renders it inline, and there is still exactly one component
// that knows what an entity looks like.
const NetworkEntityPage = () => {
    const navigate = useNavigate();
    const { entityID } = useParams();
    const { pagesContent } = usePageMeta("AssetManagement");
    const { hasPermission } = usePermissions();

    const [entity, setEntity] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [editOpen, setEditOpen] = useState(false);

    // ONE fetch, two callers: the effect on arrival, and the edit dialog on
    // success. `isCancelled` is passed in rather than closed over, so the
    // effect can guard its await and the refresh does not need to.
    const fetchEntity = useCallback(
        async (isCancelled = () => false) => {
            setIsLoading(true);
            setErrorMessage("");
            const res = await getNetworkEntity(entityID);
            // CHECKED AFTER the await, which is the only place it means
            // anything -- a flag read before the request never fires.
            if (isCancelled()) return;
            if (res?.responseStatus?.code === CODE.SUCCESS && res?.data) {
                setEntity(res.data);
            } else {
                setEntity(null);
                setErrorMessage(
                    res?.responseStatus?.msg || "Could not open this network entity"
                );
            }
            setIsLoading(false);
        },
        [entityID]
    );

    useEffect(() => {
        let cancelled = false;
        fetchEntity(() => cancelled);
        return () => {
            cancelled = true;
        };
    }, [fetchEntity]);

    // THE HOOK, not a second copy of its lookups. Both of these live at paths
    // this page has no business knowing -- types are under
    // network.entityDefinition.types, not network.entityTypes -- and reading
    // them here by hand is what made every entity render "not configured".
    // useEntityTypeConfig is the one place that knows the shape, and the
    // Network tab already went through it.
    const { types, getDetailView } = useEntityTypeConfig(pagesContent?.pageMeta);

    const entityType = useMemo(
        () => types.find((type) => type?.code === entity?.entityTypeCode) || null,
        [types, entity]
    );

    const detailView = useMemo(
        () => getDetailView(entityType),
        [getDetailView, entityType]
    );

    // BACK TO THE SUB-TAB THIS ENTITY BELONGS TO, not just to Network.
    // Returning a vendor to the Customers tab makes the reader hunt for the
    // row they were just on. entityType is read once by NetworkTabContent to
    // seed its tab and then dropped from the URL.
    const goBack = useCallback(() => {
        const code = entity?.entityTypeCode;
        navigate(
            code
                ? `/asset-management?tab=network&entityType=${encodeURIComponent(code)}`
                : "/asset-management?tab=network"
        );
    }, [navigate, entity]);

    const canEdit = hasPermission?.(
        FEATURE_CODES.ASSET_MANAGEMENT_NETWORK_ENTITIES, "update"
    );

    return (
        <ScreenContainer
            pagesContent={{ ...pagesContent, metaInfo: pagesContent?.pageMeta }}
        >
            {isLoading || !pagesContent?.mounted ? (
                // BOTH loads, one spinner. The entity comes from the API and
                // the entity TYPE from page meta, on separate schedules -- and
                // deciding "not configured" while config is still in flight is
                // an error message for a page that was going to work.
                <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                    <CircularProgress size={22} />
                </Box>
            ) : errorMessage || !entity || !entityType ? (
                // ONE STATE for "the request failed" and "the config does not
                // describe this type". Both leave nothing to render, and a
                // page that renders nothing reads as a broken page.
                <Box sx={{ px: 3, pt: 3 }}>
                    <Box
                        data-testid="network-entity-error"
                        sx={{
                            border: `1px solid ${FORM_TOKENS.fieldBorder}`,
                            borderRadius: "10px",
                            backgroundColor: FORM_TOKENS.surface,
                            p: 3,
                        }}
                    >
                        <Typography sx={{ fontSize: "14px", color: FORM_TOKENS.danger, mb: 1 }}>
                            {errorMessage || "This network entity is not configured"}
                        </Typography>
                        <CancelFormActionButton
                            data-testid="network-entity-error-back"
                            onClick={goBack}
                        >
                            Back to Network
                        </CancelFormActionButton>
                    </Box>
                </Box>
            ) : (
                <EntityDetailView
                    entityType={entityType}
                    entity={entity}
                    sections={detailView?.sections}
                    metaInfo={pagesContent?.pageMeta}
                    onBack={goBack}
                    // Named so Back says where it goes. The org's own word for
                    // the type, because that is what the tab is labelled.
                    backLabel={entityType?.labelPlural || entityType?.label || "Network"}
                    onEdit={canEdit ? () => setEditOpen(true) : undefined}
                />
            )}

            {entityType && entity ? (
                <CreateEditEntityDialog
                    open={editOpen}
                    entityType={entityType}
                    entity={entity}
                    onClose={() => setEditOpen(false)}
                    onSuccess={() => {
                        setEditOpen(false);
                        fetchEntity();
                    }}
                />
            ) : null}
        </ScreenContainer>
    );
};

export default NetworkEntityPage;
